import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Subtitles, ArrowLeft, Play, Pause, Download, Wand2,
  Loader2, AlertCircle, Crown, Volume2, VolumeX, Edit2,
  Plus, Trash2, Save, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  { value: 'center-bottom', label: 'Centro-Inferior' },
  { value: 'top-left', label: 'Superior-Esquerdo' },
  { value: 'top-right', label: 'Superior-Direito' },
  { value: 'bottom-left', label: 'Inferior-Esquerdo' },
  { value: 'bottom-right', label: 'Inferior-Direito' },
];

const FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Impact', label: 'Impact' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS' },
];

const Editor = () => {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const { user, isPro } = useAuth();
  const videoRef = useRef(null);

  const [video, setVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [transcribing, setTranscribing] = useState(false);
  const [exportingVideo, setExportingVideo] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [currentSubtitle, setCurrentSubtitle] = useState('');

  // Edit subtitle state
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ start: 0, end: 0, text: '' });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newSubtitle, setNewSubtitle] = useState({ start: 0, end: 0, text: '' });

  const [settings, setSettings] = useState({
    position: 'center-bottom',
    font_family: 'Arial',
    font_size: 24,
    box_enabled: false,
    box_opacity: 0.7
  });

  const fetchVideo = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/videos/${videoId}`, { withCredentials: true });
      setVideo(data);
      if (data.settings) {
        setSettings(data.settings);
      }
      
      // Get video URL with auth token
      const tokenResponse = await axios.get(`${API}/videos/${videoId}/token`, { withCredentials: true });
      setVideoUrl(`${API}/videos/${videoId}/stream?auth=${tokenResponse.data.token}`);
    } catch (error) {
      setError('Vídeo não encontrado');
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    fetchVideo();
  }, [fetchVideo]);

  useEffect(() => {
    if (video?.subtitles && videoRef.current) {
      const sub = video.subtitles.find(
        s => currentTime >= s.start && currentTime <= s.end
      );
      setCurrentSubtitle(sub ? sub.text : '');
    }
  }, [currentTime, video?.subtitles]);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const togglePlay = async () => {
    if (videoRef.current) {
      try {
        if (playing) {
          videoRef.current.pause();
          setPlaying(false);
        } else {
          // Wait for any pending pause to complete
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
          setPlaying(true);
        }
      } catch (error) {
        // Ignore AbortError from interrupted play/pause
        if (error.name !== 'AbortError') {
          console.error('Playback error:', error);
        }
      }
    }
  };

  const handleSeek = async (value) => {
    if (videoRef.current) {
      const wasPlaying = playing;
      
      // Pause before seeking to avoid interruption errors
      if (wasPlaying) {
        videoRef.current.pause();
        setPlaying(false);
      }
      
      videoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
      
      // Resume playing if it was playing before
      if (wasPlaying) {
        try {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
          setPlaying(true);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Seek playback error:', error);
          }
        }
      }
    }
  };

  const handleTranscribe = async () => {
    setTranscribing(true);
    setError('');

    try {
      const { data } = await axios.post(
        `${API}/transcribe`,
        { video_id: videoId, settings },
        { withCredentials: true }
      );
      
      setVideo(prev => ({
        ...prev,
        subtitles: data.subtitles,
        language_detected: data.language_detected,
        status: 'completed',
        duration: data.duration
      }));
    } catch (error) {
      setError(error.response?.data?.detail || 'Falha na transcrição');
    } finally {
      setTranscribing(false);
    }
  };

  const handleSettingsChange = async (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    // Save to backend
    try {
      await axios.put(
        `${API}/videos/${videoId}/subtitles`,
        { settings: newSettings },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleExportSRT = async () => {
    try {
      const response = await axios.get(
        `${API}/videos/${videoId}/export/srt`,
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${video.original_filename?.replace(/\.[^/.]+$/, '') || 'subtitles'}.srt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError('Falha ao exportar legendas');
    }
  };

  const handleExportVideo = async () => {
    if (!isPro) {
      setError('Exportação de vídeo requer conta Pro');
      return;
    }

    setExportingVideo(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/videos/${videoId}/export/video`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${video.original_filename?.replace(/\.[^/.]+$/, '') || 'video'}_legendado.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.response?.data?.detail || 'Falha ao exportar vídeo');
    } finally {
      setExportingVideo(false);
    }
  };

  // Subtitle editing functions
  const startEditSubtitle = (index) => {
    const sub = video.subtitles[index];
    setEditForm({
      start: sub.start,
      end: sub.end,
      text: sub.text
    });
    setEditingIndex(index);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditForm({ start: 0, end: 0, text: '' });
  };

  const saveSubtitleEdit = async () => {
    try {
      await axios.put(
        `${API}/videos/${videoId}/subtitles/${editingIndex}`,
        editForm,
        { withCredentials: true }
      );

      // Update local state
      const newSubtitles = [...video.subtitles];
      newSubtitles[editingIndex] = { ...editForm };
      setVideo(prev => ({ ...prev, subtitles: newSubtitles }));
      setEditingIndex(null);
    } catch (error) {
      setError('Falha ao salvar legenda');
    }
  };

  const deleteSubtitle = async (index) => {
    if (!window.confirm('Excluir esta legenda?')) return;

    try {
      await axios.delete(
        `${API}/videos/${videoId}/subtitles/${index}`,
        { withCredentials: true }
      );

      const newSubtitles = video.subtitles.filter((_, i) => i !== index);
      setVideo(prev => ({ ...prev, subtitles: newSubtitles }));
    } catch (error) {
      setError('Falha ao excluir legenda');
    }
  };

  const addSubtitle = async () => {
    try {
      const { data } = await axios.post(
        `${API}/videos/${videoId}/subtitles`,
        newSubtitle,
        { withCredentials: true }
      );

      setVideo(prev => ({ ...prev, subtitles: data.subtitles }));
      setShowAddDialog(false);
      setNewSubtitle({ start: 0, end: 0, text: '' });
    } catch (error) {
      setError('Falha ao adicionar legenda');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimeInput = (seconds) => {
    return seconds.toFixed(2);
  };

  const getPositionClass = () => {
    const posMap = {
      'center-bottom': 'bottom-[10%] left-1/2 -translate-x-1/2 text-center',
      'top-left': 'top-[5%] left-[5%] text-left',
      'top-right': 'top-[5%] right-[5%] text-right',
      'bottom-left': 'bottom-[10%] left-[5%] text-left',
      'bottom-right': 'bottom-[10%] right-[5%] text-right',
    };
    return posMap[settings.position] || posMap['center-bottom'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FF3B30] animate-spin" />
      </div>
    );
  }

  if (error && !video) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-white">{error}</p>
          <Button 
            onClick={() => navigate('/dashboard')}
            className="mt-4 bg-[#FF3B30] hover:bg-[#D63026]"
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              className="text-neutral-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#FF3B30] rounded-sm flex items-center justify-center">
                <Subtitles className="w-5 h-5 text-white" />
              </div>
              <span className="font-['Outfit'] text-xl font-bold text-white">Editor</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {video?.subtitles && (
              <>
                <Button
                  variant="outline"
                  data-testid="export-srt-btn"
                  onClick={handleExportSRT}
                  className="border-white/10 text-white hover:bg-white/5"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Exportar .SRT
                </Button>
                {!isPro ? (
                  <Button
                    disabled
                    data-testid="export-video-btn-disabled"
                    className="bg-neutral-700 text-neutral-400 cursor-not-allowed"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Exportar Vídeo (Pro)
                  </Button>
                ) : (
                  <Button
                    data-testid="export-video-btn"
                    onClick={handleExportVideo}
                    disabled={exportingVideo}
                    className="bg-[#FF3B30] hover:bg-[#D63026] text-white"
                  >
                    {exportingVideo ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Exportando...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Exportar Vídeo
                      </>
                    )}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-sm text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
            <button onClick={() => setError('')} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Video Player - Left Column */}
          <div className="lg:col-span-8 space-y-4">
            {/* Video Container */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-black rounded-sm overflow-hidden relative"
              style={{ maxHeight: '70vh' }}
            >
              {videoUrl ? (
                <video
                  ref={videoRef}
                  data-testid="video-player"
                  className="w-full h-auto max-h-[70vh]"
                  src={videoUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onWaiting={() => console.log('Video buffering...')}
                  onCanPlay={() => console.log('Video ready to play')}
                  onError={(e) => console.error('Video error:', e)}
                  muted={muted}
                  preload="auto"
                  playsInline
                />
              ) : (
                <div className="w-full h-64 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-[#FF3B30] animate-spin" />
                </div>
              )}

              {/* Subtitle Overlay */}
              {currentSubtitle && (
                <div
                  data-testid="subtitle-overlay"
                  className={`absolute ${getPositionClass()} max-w-[90%] pointer-events-none`}
                  style={{ fontFamily: settings.font_family }}
                >
                  {settings.box_enabled ? (
                    <span
                      className="inline-block px-4 py-2 rounded-lg"
                      style={{
                        fontSize: `${settings.font_size}px`,
                        backgroundColor: `rgba(0, 0, 0, ${settings.box_opacity})`,
                        color: 'white',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                      }}
                    >
                      {currentSubtitle}
                    </span>
                  ) : (
                    <span
                      style={{
                        fontSize: `${settings.font_size}px`,
                        color: 'white',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8)'
                      }}
                    >
                      {currentSubtitle}
                    </span>
                  )}
                </div>
              )}
            </motion.div>

            {/* Video Controls */}
            <div className="bg-[#141414] border border-white/10 rounded-sm p-4 space-y-4">
              {/* Progress bar */}
              <Slider
                data-testid="video-progress-slider"
                value={[currentTime]}
                min={0}
                max={duration || 100}
                step={0.1}
                onValueChange={handleSeek}
                className="cursor-pointer"
              />

              {/* Controls row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="play-pause-btn"
                    onClick={togglePlay}
                    className="text-white hover:bg-white/10"
                  >
                    {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    data-testid="mute-btn"
                    onClick={() => setMuted(!muted)}
                    className="text-white hover:bg-white/10"
                  >
                    {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </Button>
                  <span className="text-sm text-neutral-400 ml-2">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>

                {video?.language_detected && (
                  <span className="text-sm text-neutral-400">
                    Idioma detectado: <span className="text-white font-medium">{video.language_detected.toUpperCase()}</span>
                  </span>
                )}
              </div>
            </div>

            {/* Transcribe Button (if no subtitles) */}
            {!video?.subtitles && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#141414] border border-white/10 rounded-sm p-8 text-center"
              >
                <Wand2 className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
                <h3 className="font-['Outfit'] text-xl font-bold text-white mb-2">
                  Gerar Legendas com IA
                </h3>
                <p className="text-neutral-400 text-sm mb-6">
                  O OpenAI Whisper irá detectar automaticamente o idioma e transcrever seu vídeo
                </p>
                <Button
                  data-testid="transcribe-btn"
                  onClick={handleTranscribe}
                  disabled={transcribing}
                  className="bg-[#FF3B30] hover:bg-[#D63026] text-white px-8"
                >
                  {transcribing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Transcrevendo...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-2" />
                      Transcrever Vídeo
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            {/* Subtitles Timeline (if has subtitles) */}
            {video?.subtitles && video.subtitles.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden"
              >
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <h3 className="font-['Outfit'] font-bold text-white">Timeline de Legendas</h3>
                  <Button
                    size="sm"
                    data-testid="add-subtitle-btn"
                    onClick={() => {
                      setNewSubtitle({ start: currentTime, end: currentTime + 2, text: '' });
                      setShowAddDialog(true);
                    }}
                    className="bg-[#FF3B30] hover:bg-[#D63026] text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {video.subtitles.map((sub, index) => {
                    const isActive = currentTime >= sub.start && currentTime <= sub.end;
                    const isEditing = editingIndex === index;

                    return (
                      <div
                        key={index}
                        data-testid={`subtitle-item-${index}`}
                        className={`p-3 border-b border-white/5 transition-colors ${
                          isActive ? 'bg-[#FF3B30]/10 border-l-2 border-l-[#FF3B30]' : 'hover:bg-white/5'
                        }`}
                      >
                        {isEditing ? (
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <div className="flex-1">
                                <Label className="text-xs text-neutral-400">Início (s)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={editForm.start}
                                  onChange={(e) => setEditForm({ ...editForm, start: parseFloat(e.target.value) })}
                                  className="bg-[#1E1E1E] border-white/10 text-white h-8"
                                />
                              </div>
                              <div className="flex-1">
                                <Label className="text-xs text-neutral-400">Fim (s)</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={editForm.end}
                                  onChange={(e) => setEditForm({ ...editForm, end: parseFloat(e.target.value) })}
                                  className="bg-[#1E1E1E] border-white/10 text-white h-8"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-neutral-400">Texto</Label>
                              <Input
                                value={editForm.text}
                                onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
                                className="bg-[#1E1E1E] border-white/10 text-white"
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEdit}
                                className="text-neutral-400 hover:text-white"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Cancelar
                              </Button>
                              <Button
                                size="sm"
                                onClick={saveSubtitleEdit}
                                className="bg-[#FF3B30] hover:bg-[#D63026] text-white"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                Salvar
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <span 
                              className="text-xs text-neutral-500 font-mono cursor-pointer hover:text-white"
                              onClick={() => handleSeek([sub.start])}
                            >
                              {formatTime(sub.start)}
                            </span>
                            <span className={`flex-1 text-sm ${isActive ? 'text-white' : 'text-neutral-300'}`}>
                              {sub.text}
                            </span>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`edit-subtitle-${index}`}
                                onClick={() => startEditSubtitle(index)}
                                className="h-8 w-8 text-neutral-400 hover:text-white hover:bg-white/10"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                data-testid={`delete-subtitle-${index}`}
                                onClick={() => deleteSubtitle(index)}
                                className="h-8 w-8 text-neutral-400 hover:text-red-500 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* Settings Sidebar - Right Column */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#141414] border border-white/10 rounded-sm sticky top-6"
            >
              <div className="p-4 border-b border-white/10">
                <h3 className="font-['Outfit'] font-bold text-white">Personalização</h3>
              </div>

              <div className="p-4 space-y-6">
                {/* Position */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                    Posição
                  </Label>
                  <Select
                    value={settings.position}
                    onValueChange={(v) => handleSettingsChange('position', v)}
                  >
                    <SelectTrigger 
                      data-testid="position-select"
                      className="bg-[#1E1E1E] border-white/10 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10">
                      {POSITIONS.map((pos) => (
                        <SelectItem 
                          key={pos.value} 
                          value={pos.value}
                          className="text-white hover:bg-white/10"
                        >
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Font Family */}
                <div className="space-y-2">
                  <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                    Fonte
                  </Label>
                  <Select
                    value={settings.font_family}
                    onValueChange={(v) => handleSettingsChange('font_family', v)}
                  >
                    <SelectTrigger 
                      data-testid="font-select"
                      className="bg-[#1E1E1E] border-white/10 text-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E1E1E] border-white/10">
                      {FONTS.map((font) => (
                        <SelectItem 
                          key={font.value} 
                          value={font.value}
                          className="text-white hover:bg-white/10"
                          style={{ fontFamily: font.value }}
                        >
                          {font.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Font Size */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                      Tamanho da Fonte
                    </Label>
                    <span className="text-sm text-white font-mono">{settings.font_size}px</span>
                  </div>
                  <Slider
                    data-testid="font-size-slider"
                    value={[settings.font_size]}
                    min={12}
                    max={48}
                    step={1}
                    onValueChange={([v]) => handleSettingsChange('font_size', v)}
                  />
                </div>

                {/* Box Toggle */}
                <div className="space-y-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                      Fundo da Legenda
                    </Label>
                    <Switch
                      data-testid="box-toggle"
                      checked={settings.box_enabled}
                      onCheckedChange={(v) => handleSettingsChange('box_enabled', v)}
                    />
                  </div>

                  {/* Box Opacity */}
                  {settings.box_enabled && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                          Opacidade do Fundo
                        </Label>
                        <span className="text-sm text-white font-mono">
                          {Math.round(settings.box_opacity * 100)}%
                        </span>
                      </div>
                      <Slider
                        data-testid="box-opacity-slider"
                        value={[settings.box_opacity]}
                        min={0.1}
                        max={1}
                        step={0.1}
                        onValueChange={([v]) => handleSettingsChange('box_opacity', v)}
                      />
                    </div>
                  )}
                </div>

                {/* Preview */}
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <Label className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                    Preview
                  </Label>
                  <div className="bg-black rounded-sm p-8 flex items-center justify-center min-h-[100px]">
                    {settings.box_enabled ? (
                      <span
                        className="px-4 py-2 rounded-lg"
                        style={{
                          fontFamily: settings.font_family,
                          fontSize: `${Math.min(settings.font_size, 20)}px`,
                          backgroundColor: `rgba(0, 0, 0, ${settings.box_opacity})`,
                          color: 'white'
                        }}
                      >
                        Exemplo de legenda
                      </span>
                    ) : (
                      <span
                        style={{
                          fontFamily: settings.font_family,
                          fontSize: `${Math.min(settings.font_size, 20)}px`,
                          color: 'white',
                          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
                        }}
                      >
                        Exemplo de legenda
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Add Subtitle Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#141414] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Adicionar Legenda</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-neutral-400">Início (segundos)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newSubtitle.start}
                  onChange={(e) => setNewSubtitle({ ...newSubtitle, start: parseFloat(e.target.value) || 0 })}
                  className="bg-[#1E1E1E] border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-neutral-400">Fim (segundos)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={newSubtitle.end}
                  onChange={(e) => setNewSubtitle({ ...newSubtitle, end: parseFloat(e.target.value) || 0 })}
                  className="bg-[#1E1E1E] border-white/10 text-white"
                />
              </div>
            </div>
            <div>
              <Label className="text-neutral-400">Texto da Legenda</Label>
              <Input
                value={newSubtitle.text}
                onChange={(e) => setNewSubtitle({ ...newSubtitle, text: e.target.value })}
                placeholder="Digite o texto da legenda..."
                className="bg-[#1E1E1E] border-white/10 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowAddDialog(false)}
              className="text-neutral-400 hover:text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={addSubtitle}
              disabled={!newSubtitle.text}
              className="bg-[#FF3B30] hover:bg-[#D63026] text-white"
            >
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Editor;
